import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import {
  login,
  logout,
  selectUser,
  selectUserLoading,
} from "../../store/slices/userSlice";
import { requestPermissionAndGetToken } from "../../services/notifications";

const redirectFromLocation = (location) => {
  const from = location.state?.from;
  if (typeof from === "string" && from.startsWith("/") && !from.startsWith("//")) return from;
  if (from?.pathname?.startsWith("/") && !from.pathname.startsWith("//")) {
    return `${from.pathname}${from.search || ""}${from.hash || ""}`;
  }
  return "/";
};

const registerPushToken = async (authToken) => {
  try {
    const fcmToken = await requestPermissionAndGetToken();
    if (!fcmToken) return;

    const apiBase = process.env.REACT_APP_API_URL || "https://www.covrly.net/api/v1";
    await fetch(`${apiBase}/users/fcm/save-token`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({ token: fcmToken, platform: "web" }),
    });
  } catch {
    // Push is optional and must not hold the authenticated session open.
  }
};

export default function LoginPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector(selectUser);
  const isLoading = useSelector(selectUserLoading);
  const loginInProgress = useRef(false);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [formError, setFormError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const redirectTarget = redirectFromLocation(location);

  useEffect(() => {
    if (user?.role === "admin" && !loginInProgress.current) {
      navigate(redirectTarget, { replace: true });
    }
  }, [navigate, redirectTarget, user]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError("");

    const normalizedEmail = email.trim();
    if (!normalizedEmail || !password) {
      const message = "Enter both your email address and password";
      setFormError(message);
      toast.error(message);
      return;
    }

    loginInProgress.current = true;
    setSubmitting(true);
    try {
      const response = await dispatch(login({ email: normalizedEmail, password }));
      const data = response?.data || response;
      const loggedUser = data?.user;
      const authToken = data?.token;

      if (!loggedUser || !authToken) {
        throw new Error("The login response was incomplete. Please try again.");
      }

      if (loggedUser.role !== "admin") {
        const message = "Access denied. This sign-in is for administrators only.";
        dispatch(logout());
        setFormError(message);
        toast.error(message);
        return;
      }

      toast.success("Login successful. Welcome back.");
      navigate(redirectTarget, { replace: true });
      void registerPushToken(authToken);
    } catch (loginError) {
      const message = loginError?.message || "Login failed. Please try again.";
      setFormError(message);
      toast.error(message);
    } finally {
      loginInProgress.current = false;
      setSubmitting(false);
    }
  };

  return (
    <main className="grid min-h-[100dvh] bg-gray-50 lg:grid-cols-2">
      <section className="relative hidden overflow-hidden bg-gray-950 p-10 text-white lg:flex lg:flex-col lg:justify-between xl:p-14" aria-label="Covrly administration overview">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(245,158,11,0.28),_transparent_45%)]" aria-hidden="true" />
        <div className="relative flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-500" aria-hidden="true">
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7Z" /></svg>
          </div>
          <span className="text-xl font-semibold tracking-wide">Covrly Admin</span>
        </div>

        <div className="relative max-w-xl">
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-amber-400">Administration workspace</p>
          <h1 className="mt-4 text-4xl font-semibold leading-tight xl:text-5xl">Operate the Covrly platform with clarity.</h1>
          <p className="mt-5 text-lg leading-relaxed text-gray-300">
            Review platform activity, manage subscriptions and content, and respond to customer support from one secure workspace.
          </p>
          <ul className="mt-8 grid gap-3 text-sm text-gray-200 sm:grid-cols-2">
            {["Account oversight", "Subscription operations", "Content management", "Support workflows"].map((item) => (
              <li key={item} className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-3">
                <span className="h-2 w-2 rounded-full bg-amber-400" aria-hidden="true" />
                {item}
              </li>
            ))}
          </ul>
        </div>

        <p className="relative text-sm text-gray-400">Authorized administrators only.</p>
      </section>

      <section className="flex items-center justify-center p-4 sm:p-8 lg:p-12">
        <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-6 shadow-xl sm:p-8 lg:border-0 lg:shadow-none">
          <div className="mb-8 flex items-center justify-center gap-3 lg:justify-start">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-500 text-white" aria-hidden="true">
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7Z" /></svg>
            </div>
            <span className="text-xl font-bold text-gray-900">Covrly Admin</span>
          </div>

          <div className="mb-8 text-center lg:text-left">
            <h2 className="text-3xl font-bold text-gray-900">Welcome back</h2>
            <p className="mt-2 text-sm text-gray-500">Sign in with your administrator account.</p>
          </div>

          {formError && (
            <div className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
              {formError}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="email" className="mb-2 block text-sm font-medium text-gray-700">Email address</label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                maxLength={254}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                disabled={isLoading || submitting}
                placeholder="admin@example.com"
                className="block w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-gray-900 shadow-sm outline-none placeholder:text-gray-400 focus:border-amber-500 focus:ring-2 focus:ring-amber-100 disabled:cursor-not-allowed disabled:bg-gray-100"
              />
            </div>

            <div>
              <label htmlFor="password" className="mb-2 block text-sm font-medium text-gray-700">Password</label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  disabled={isLoading || submitting}
                  placeholder="Enter your password"
                  className="block w-full rounded-xl border border-gray-300 bg-white py-3 pl-4 pr-12 text-gray-900 shadow-sm outline-none placeholder:text-gray-400 focus:border-amber-500 focus:ring-2 focus:ring-amber-100 disabled:cursor-not-allowed disabled:bg-gray-100"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((visible) => !visible)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  aria-pressed={showPassword}
                  className="absolute inset-y-0 right-0 flex w-12 items-center justify-center rounded-r-xl text-gray-500 hover:text-amber-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-amber-500"
                >
                  {showPassword ? (
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.5 12C3.7 8 7.5 5 12 5s8.3 3 9.5 7c-1.2 4-5 7-9.5 7s-8.3-3-9.5-7Z" /></svg>
                  ) : (
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m3 3 18 18M10.6 10.6a2 2 0 0 0 2.8 2.8M9.9 4.2A10.5 10.5 0 0 1 12 4c4.5 0 8.3 3.1 9.5 8a11.6 11.6 0 0 1-2.1 4.1M6.2 6.2A11.3 11.3 0 0 0 2.5 12c1.2 4.9 5 8 9.5 8 1.4 0 2.7-.3 3.9-.8" /></svg>
                  )}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading || submitting}
              className="flex w-full items-center justify-center rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 px-4 py-3 font-semibold text-white shadow-lg transition hover:from-amber-600 hover:to-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isLoading || submitting ? (
                <><span className="mr-3 h-5 w-5 animate-spin rounded-full border-2 border-white/40 border-t-white" aria-hidden="true" />Signing in…</>
              ) : "Sign in"}
            </button>
          </form>

          <p className="mt-6 text-center text-xs leading-relaxed text-gray-500">
            By signing in, you agree to our <Link to="/terms-of-service" className="font-medium text-amber-700 hover:underline">Terms of Service</Link> and <Link to="/privacy-policy" className="font-medium text-amber-700 hover:underline">Privacy Policy</Link>.
          </p>
        </div>
      </section>
    </main>
  );
}
